import { useTranslation } from "@/hooks/useTranslation";
import { splitCommission } from "../campaignConstants";

/**
 * The three numbers behind any rate, shown wherever a rate is.
 *
 * Commission is charged on top of the creator's fee — the creator keeps 100% of
 * what they negotiate — so the number the brand briefs and the number the brand
 * pays are genuinely different. An agency that briefs US$ 1.000 and discovers
 * US$ 1.180 at payment time loses trust at exactly the moment they are being
 * charged, so all three are always on screen rather than behind a tooltip.
 *
 * Each side sees its own number emphasised: the creator cares what lands, the
 * brand cares what leaves.
 */
export default function CommissionSplit({
  rate,
  currency,
  commissionPct,
  viewerParty,
  compact = false,
}: {
  rate: number;
  currency: string;
  commissionPct: number;
  viewerParty: "BRAND" | "CREATOR";
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const split = splitCommission(rate, commissionPct);

  const money = (value: number) =>
    `${currency} ${value.toLocaleString("es", { maximumFractionDigits: 2 })}`;

  const rows = [
    {
      key: "creator",
      label: t("marketplace.collaboration.split.creatorGets"),
      value: split.creatorPayout,
      strong: viewerParty === "CREATOR",
    },
    {
      key: "fee",
      label: `${t("marketplace.collaboration.split.platformFee")} (${commissionPct}%)`,
      value: split.commission,
      strong: false,
    },
    {
      key: "brand",
      label: t("marketplace.collaboration.split.brandPays"),
      value: split.brandTotal,
      strong: viewerParty === "BRAND",
    },
  ];

  return (
    <dl className={compact ? "space-y-0.5" : "space-y-1"}>
      {rows.map((row, i) => (
        <div
          key={row.key}
          className={`flex items-baseline justify-between gap-3 ${
            i === rows.length - 1 ? "pt-1 border-t border-neutral-100" : ""
          }`}
        >
          <dt className={`text-xs ${row.strong ? "text-neutral-700" : "text-neutral-500"}`}>
            {row.label}
          </dt>
          <dd
            className={`tabular-nums whitespace-nowrap ${
              row.strong
                ? "text-sm font-semibold text-neutral-900"
                : "text-xs text-neutral-600"
            }`}
          >
            {money(row.value)}
          </dd>
        </div>
      ))}
    </dl>
  );
}
